import { SIDEBAR_COLLAPSIBLE } from '@/shared/config/sidebar';
import { useSidebarStore } from '@/shared/stores/sidebarStore';
import type { Collapsible } from '@/shared/types/sidebar';

export type SidebarApi = {
  /** Primary rail expanded (`true`) or collapsed to icons (`false`). */
  open: boolean;
  setOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  collapsible: Collapsible;
};

/**
 * The sidebar's runtime API: the desktop open/collapsed flag from the Zustand
 * store (`useSidebarStore`) plus the fixed `collapsible` mode. The primary rail is
 * persistent at every width — `toggleSidebar` flips the desktop `open` flag (there
 * is no off-canvas drawer; the mobile nav is the `mobile-nav` overlay dialog).
 * Any subscribing component re-renders only when the slice it reads changes.
 * There is no provider — the store is global. Components that need the responsive
 * flag read `useIsMobile()` directly; mobile-vs-desktop density is passed down as
 * a prop from the mount point, not read here.
 */
export function useSidebar(): SidebarApi {
  const open = useSidebarStore((s) => s.open);
  const setOpen = useSidebarStore((s) => s.setOpen);
  const toggleSidebar = useSidebarStore((s) => s.toggleOpen);

  return {
    open,
    setOpen,
    toggleSidebar,
    // Fixed layout config — the app never varies it.
    collapsible: SIDEBAR_COLLAPSIBLE,
  };
}
